import { ResponsiveContainer, LineChart, Line, YAxis } from "recharts";
import { useNovexData } from "@/hooks/useNovexData";
import { useLang } from "@/context/LanguageContext";
import { ExplainButton } from "@/components/ExplainButton";
import { SourceBadge } from "@/components/SourceBadge";

export function RiskGauge({ compact = false }) {
  const { t } = useLang();
  const zone = (score) => {
    if (score == null) return { label: "—", color: "#64748B" };
    if (score < 40) return { label: t("c.riskOff"), color: "#EF4444" };
    if (score <= 60) return { label: t("c.riskNeutral"), color: "#F59E0B" };
    return { label: t("c.riskOn"), color: "#10B981" };
  };
  const { status, data, error, source } = useNovexData("/novex/risk-sentiment");

  if (status === "loading") return <div className="h-40 animate-pulse rounded-md bg-slate-800/50" />;
  if (error) return <p className="text-sm text-amber-500/80">{error}</p>;

  const rs = data?.risk_sentiment || {};
  const evo = Array.isArray(rs.evolution) ? rs.evolution : [];
  const score = rs.score != null ? Math.round(rs.score) : (evo.length ? Math.round(evo[evo.length - 1].score) : null);
  const z = zone(score);
  const pct = Math.max(0, Math.min(100, score ?? 0));
  const ctxText = rs.rating
    ? t("c.fgIndex", { rating: t(`c.r${rs.rating}`) })
    : rs.contexte;

  return (
    <div>
      <div className="mb-2 flex justify-end"><SourceBadge source={source} /></div>
      <div className="flex items-end justify-between gap-4">
        <div>
          <div className="font-mono text-5xl font-bold tracking-tighter" style={{ color: z.color }} data-testid="risk-score">
            {score ?? "—"}<span className="text-lg text-slate-600">/100</span>
          </div>
          <div className="mt-1 text-xs font-semibold uppercase tracking-wider" style={{ color: z.color }}>{z.label}</div>
        </div>
        {evo.length > 2 && (
          <div className="h-12 w-28">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={evo.slice(-40)}>
                <YAxis domain={[0, 100]} hide />
                <Line type="monotone" dataKey="score" stroke={z.color} strokeWidth={1.5} dot={false} isAnimationActive={false} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>

      <div className="relative mt-4 h-2.5 w-full overflow-hidden rounded-full" style={{ background: "linear-gradient(90deg,#EF4444 0%,#F59E0B 50%,#10B981 100%)" }}>
        <div className="absolute inset-0 bg-black/40" style={{ clipPath: `inset(0 0 0 ${pct}%)` }} />
      </div>
      <div className="mt-1 flex justify-between font-mono text-[10px] text-slate-600">
        <span>0 · {t("c.fear")}</span><span>50</span><span>{t("c.greed")} · 100</span>
      </div>

      {ctxText && (
        <div className="mt-4 border-t border-[#222733] pt-3">
          <p className={`text-sm leading-relaxed text-slate-400 ${compact ? "line-clamp-4" : ""}`}>{ctxText}</p>
          <div className="mt-2 flex justify-end">
            <ExplainButton kind="sentiment de risque" text={`Sentiment de risque: ${score}/100. ${ctxText}`} testid="explain-risk" label={t("c.explain")} />
          </div>
        </div>
      )}
    </div>
  );
}
